/**
 * RezervaceKalendar — the car × day month view of the reservations (P3-c): one row per fleet car, one column per
 * day, a bar per reservation (the customer, or "Technická"). It only draws what the page already loaded — the
 * fleet list and the month's reservations — and hands clicks back: an empty day of a car creates a reservation
 * there, a bar opens it. Overlapping reservations of one car (an admin booked through a collision) stack in lanes.
 *
 * Built on the shared swimlane the servis planner uses (`Calendar.SwimGrid` / `SwimSlotHeader` / `SwimRow` with
 * `slotDisabled`, `EventChip`) so it looks and behaves like it: a day that has passed is simply not clickable (a new
 * reservation cannot start in the past), today is the accent in the header, a weekend is named by its weekday letter
 * in full ink. Today's column and the weekend columns carry a barely-there background (the soft tokens at reduced
 * opacity, under the bars); a car without a plate shows its VIN, smaller so it fits.
 */

import { useMemo } from 'react';
import { Calendar } from '@/components/Calendar';
import { ColorSwatch, vehicleColorToHex } from '@/components/ColorSwatch';
import { EventChip, type EventChipTone } from '@/components/EventChip';
import type { ServisKalendarUdalost } from '@/modules/servis/api';
import type { FleetEntry, Rezervace } from './api';
import { VozidloOdkaz } from './VozidloOdkaz';
import { nazevUdalosti, popisUdalosti, tonUdalosti } from './servis-udalosti-helpers';
import { assignLanes, barPlace, daysOfMonth, formatTermin, konecProZobrazeni, jeUkoncena, podtitulekRezervace, seradFlotilu, tonRezervace, vozidloLabel, weekdayOf } from './rezervace-helpers';

/** The least height a lane needs: a row grows with the reservations stacked in it, but a row without overlaps is the 76 px minimum
 * (the car's cell has three lines: plate, make / model with its colour dot, engine). */
const LANE = 32;
const ROW_MIN = 76;
/** Space above and below the bars in a row — the servis planner's chips use the same 4 px (`inset-y-1`). */
const ROW_PAD = 4;
const WEEKDAY = ['p', 'ú', 's', 'č', 'p', 's', 'n'];
/** How much of the soft token shows: today and the weekends are only hinted at. */
const TINT_OPACITY = 0.55;

interface Props {
  fleet: FleetEntry[];
  rezervace: Rezervace[];
  /** `YYYY-MM`. */
  month: string;
  /** Prague today, `YYYY-MM-DD` — its column header is the accent and ended bars keep their colour, faded. */
  today: string;
  /** The servis events of each fleet car (entry id → events) — read-only bars; none for a member without `servis:read`. */
  servisUdalosti?: ReadonlyMap<number, ServisKalendarUdalost[]>;
  canWrite: boolean;
  onCreate: (idVozidla: number, day: string) => void;
  onOpen: (r: Rezervace) => void;
  /** A servis bar was clicked: the page opens the event's detail, read-only. */
  onOpenServis?: (idUdalosti: number) => void;
}

export function RezervaceKalendar({ fleet, rezervace, month, today, servisUdalosti, canWrite, onCreate, onOpen, onOpenServis }: Props) {
  const days = useMemo(() => daysOfMonth(month), [month]);
  const byCar = useMemo(() => {
    const m = new Map<number, Rezervace[]>();
    for (const r of rezervace) {
      if (r.stav !== 'aktivni') continue;
      const list = m.get(r.id_vozidla) ?? [];
      list.push(r);
      m.set(r.id_vozidla, list);
    }
    return m;
  }, [rezervace]);

  // One row per fleet car, then one per car that left the fleet but still has reservations in the month (ended
  // ones stay active records): it cannot be booked any more, so its days are no creation targets.
  const rows = useMemo(() => {
    const inFleet = new Set(fleet.map((f) => f.id));
    const removed = [...byCar.entries()]
      .filter(([id]) => !inFleet.has(id))
      .map(([id, list]) => ({ id, vin: list[0]!.vin }))
      .sort((a, b) => a.vin.localeCompare(b.vin));
    // the fleet in the shared order (make and model, then plate); the cars that left the fleet stay at the end
    const vozy = seradFlotilu(fleet).map((car) => ({
      id: car.id,
      entry: car,
      removed: false,
      title: car.vozidlo?.spz || car.vin,
      bezSpz: !car.vozidlo?.spz,
      sub: [car.vozidlo?.znacka_nazev, car.vozidlo?.model_nazev].filter(Boolean).join(' ') || 'Vozidlo',
      barva: car.vozidlo?.barva ?? '',
      motor: car.vozidlo?.motor ?? '',
      pobocka: car.pobocka_nazev,
      aria: vozidloLabel(car),
    }));
    return [
      ...vozy,
      ...removed.map((c) => ({ id: c.id, entry: null, removed: true, title: c.vin, bezSpz: true, sub: 'Vyřazeno z půjčovny', barva: '', motor: '', pobocka: '', aria: c.vin })),
    ];
  }, [fleet, byCar]);

  const header = days.map((d) => {
    const wd = weekdayOf(d);
    const dnes = d === today;
    return (
      <span
        key={d}
        data-testid={`kal-den-${d}`}
        className={
          'flex flex-col items-start leading-tight ' +
          (dnes ? 'font-bold text-accent-text' : wd >= 5 ? 'font-bold text-ink' : 'font-normal text-ink-2')
        }
      >
        <span>{Number(d.slice(8))}</span>
        <span className="text-[9px] uppercase">{WEEKDAY[wd]}</span>
      </span>
    );
  });

  return (
    <div data-testid="rezervace-kalendar" className="flex min-h-0 flex-col">
      <Calendar scroll>
        <Calendar.SwimGrid resourceCols={['150px', '96px']} slots={days.length} slotMinPx={30} minWidth={150 + 96 + days.length * 30}>
          <Calendar.SwimCorner>Vozidlo</Calendar.SwimCorner>
          <Calendar.SwimSlotHeader slots={header} majorEvery={1} />

          {rows.map((car) => {
            const items = byCar.get(car.id) ?? [];
            const placed = items
              .map((r) => ({ r, place: barPlace({ od: r.od, do: konecProZobrazeni(r, today) }, days) }))
              .filter((x): x is { r: Rezervace; place: NonNullable<ReturnType<typeof barPlace>> } => x.place != null);
            // the car's servis events (read-only) share the row: they stack in lanes with the reservations but never block a day
            const servisPlaced = (servisUdalosti?.get(car.id) ?? [])
              .map((e) => ({ e, place: barPlace({ od: e.datum_od, do: e.datum_do }, days) }))
              .filter((x): x is { e: ServisKalendarUdalost; place: NonNullable<ReturnType<typeof barPlace>> } => x.place != null);
            const lanes = assignLanes([...placed.map((p) => p.place), ...servisPlaced.map((p) => p.place)]);
            const laneCount = Math.max(1, ...lanes.map((l) => l + 1));
            // the bars fill the row like the servis planner's chips do: one lane = the whole row, stacked lanes share it
            const rowPx = Math.max(ROW_MIN, 2 * ROW_PAD + laneCount * LANE);
            const laneH = (rowPx - 2 * ROW_PAD) / laneCount;
            // a day under a reservation is taken: only a free day is a creation target (else a guaranteed collision)
            const taken = (i: number) => placed.some((x) => x.place.from <= i && i <= x.place.to);
            return (
              <Calendar.SwimDay key={car.id} rows={1} dayLabel={
                  // no plate: the VIN is the title, set small enough for the column
                  <span data-testid={`kal-vuz-${car.id}`} className={car.bezSpz ? 'font-mono text-[11px] font-semibold tracking-[-0.03em]' : ''}>
                    <VozidloOdkaz entry={car.entry}>{car.title}</VozidloOdkaz>
                  </span>
                } dayDate={
                  <span className="flex flex-col gap-0.5">
                    <span data-testid={`kal-model-${car.id}`} className="flex min-w-0 items-center gap-1.5" title={car.barva ? `${car.sub} · ${car.barva}` : car.sub}>
                      {car.barva && <ColorSwatch color={vehicleColorToHex(car.barva)} size={8} />}
                      <span className="truncate">{car.sub}</span>
                    </span>
                    {car.motor && <span data-testid={`kal-motor-${car.id}`} className="truncate" title={car.motor}>{car.motor}</span>}
                  </span>
                }>
                <Calendar.SwimRow
                  slots={days.length}
                  rowPx={rowPx}
                  resourceLeft={150}
                  resource={<span className="text-[11.5px] text-ink-2">{car.pobocka || '—'}</span>}
                  onSlotClick={canWrite && !car.removed ? (i) => onCreate(car.id, days[i]!) : undefined}
                  slotDisabled={(i) => days[i]! < today || taken(i)}
                  slotLabel={(i) => `Nová rezervace ${car.aria} ${days[i]}`}
                  slotTestId={(i) => `kal-bunka-${car.id}-${days[i]}`}
                >
                  {days.map((d, i) => {
                    const dnes = d === today;
                    if (!dnes && weekdayOf(d) < 5) return null;
                    return (
                      <span
                        key={d}
                        aria-hidden
                        className={'pointer-events-none absolute inset-y-0 ' + (dnes ? 'bg-accent-soft' : 'bg-neutral-soft')}
                        style={{ left: `calc(${i} * (100% / ${days.length}))`, width: `calc(100% / ${days.length})`, opacity: TINT_OPACITY }}
                      />
                    );
                  })}
                  {placed.map(({ r, place }, k) => {
                    const technical = r.typ === 'technicky';
                    const ended = jeUkoncena(r, today);
                    const label = r.typ === 'servis' ? 'Servis' : technical ? 'Technická' : (r.zakaznik_nazev ?? '—');
                    // red = out past its return or in the workshop (an error state, like a failed check), grey = a technical block, else by středisko: green rental,
                    // blue servis, amber employees. What has ended keeps its colour and is faded (the tint and the edge, never the text: AA)
                    const tone: EventChipTone = tonRezervace(r);
                    const title = `${label}${r.zakaznik_telefon ? ` (${r.zakaznik_telefon})` : ''} · ${formatTermin(r.od, r.do)}${r.po_terminu ? ' · po termínu, vozidlo je venku' : ended ? ' · ukončeno' : ''}`;
                    const sub = podtitulekRezervace(r);
                    // a bar that has the whole row has two lines; stacked lanes have room for one, so the details follow the name
                    // on that line (and are the first thing a narrow bar cuts off)
                    const dvouradkova = laneCount === 1;
                    return (
                      <EventChip
                        key={r.id}
                        testId={`kal-rez-${r.id}`}
                        tone={tone}
                        faded={ended}
                        title={
                          dvouradkova ? (
                            `${place.cutBefore ? '‹ ' : ''}${label}${place.cutAfter ? ' ›' : ''}`
                          ) : (
                            <>
                              {place.cutBefore ? '‹ ' : ''}
                              {label}
                              <span className="font-normal text-ink-2"> · {sub}</span>
                              {place.cutAfter ? ' ›' : ''}
                            </>
                          )
                        }
                        sub={dvouradkova ? sub : undefined}
                        hoverTitle={title}
                        start={place.from}
                        span={place.to - place.from + 1}
                        totalSlots={days.length}
                        // every bar opens its reservation; the dialog itself shows it read-only where nothing can be changed (returned, car left the fleet, read access)
                        onClick={() => onOpen(r)}
                        style={{ top: ROW_PAD + lanes[k]! * laneH, bottom: 'auto', height: laneCount > 1 ? laneH - 2 : laneH }}
                        className="justify-center py-0"
                      />
                    );
                  })}
                  {servisPlaced.map(({ e, place }, k) => {
                    const dvouradkova = laneCount === 1;
                    const label = `${place.cutBefore ? '‹ ' : ''}${popisUdalosti(e)}${place.cutAfter ? ' ›' : ''}`;
                    return (
                      <EventChip
                        key={`s${e.id_udalosti}`}
                        testId={`kal-servis-${e.id_udalosti}`}
                        tone={tonUdalosti(e)}
                        icon="wrench"
                        dashed
                        faded={e.datum_do.slice(0, 10) < today}
                        status={e.potvrzeni_stav ?? null}
                        title={label}
                        sub={dvouradkova ? e.datum_od.slice(11, 16) + ' → ' + e.datum_do.slice(11, 16) : undefined}
                        hoverTitle={nazevUdalosti(e)}
                        start={place.from}
                        span={place.to - place.from + 1}
                        totalSlots={days.length}
                        onClick={onOpenServis ? () => onOpenServis(e.id_udalosti) : undefined}
                        style={{ top: ROW_PAD + lanes[placed.length + k]! * laneH, bottom: 'auto', height: laneCount > 1 ? laneH - 2 : laneH }}
                        className="justify-center py-0"
                      />
                    );
                  })}
                </Calendar.SwimRow>
              </Calendar.SwimDay>
            );
          })}
        </Calendar.SwimGrid>
      </Calendar>
    </div>
  );
}
